import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SchemaMappings, SenderProfile } from '../../../lib/api';
import { partnerApi } from '../../../lib/api';

const getSchemaProperties = (
  schemas: Awaited<ReturnType<typeof partnerApi.getSchemaDefinitions>>,
  schemaId: string,
): string[] => {
  const schema = schemas.find(s => s.id === schemaId);
  if (!schema) return [];
  return Object.keys((schema.schema.properties as Record<string, unknown>) || {});
};

export function useSenderProfiles() {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedMappings, setEditedMappings] = useState<SchemaMappings | null>(null);
  const [saveError, setSaveError] = useState('');

  const { data: schemas = [] } = useQuery({
    queryKey: ['schemaDefinitions'],
    queryFn: () => partnerApi.getSchemaDefinitions(),
  });

  const { data: mappings, isLoading } = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: SchemaMappings) => partnerApi.updateSchemaMappings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemaMappings'] });
      setIsEditing(false);
      setSaveError('');
    },
    onError: () => {
      setSaveError('Failed to save. Please try again.');
    },
  });

  const handleEdit = () => {
    setEditedMappings(mappings ?? null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedMappings(null);
    setSaveError('');
  };

  const handleSave = () => {
    if (!editedMappings) return;
    updateMutation.mutate(editedMappings);
  };

  const replacePrimaryProfiles = (next: SenderProfile[]) => {
    if (!editedMappings?.contactsSchema?.primarySource) return;
    setEditedMappings({
      ...editedMappings,
      contactsSchema: {
        ...editedMappings.contactsSchema,
        primarySource: {
          ...editedMappings.contactsSchema.primarySource,
          senderProfiles: next.length > 0 ? next : undefined,
        },
      },
    });
  };

  const activeMappings = isEditing ? editedMappings : mappings;

  const addProfile = () => {
    const existing = activeMappings?.contactsSchema?.primarySource?.senderProfiles ?? [];
    replacePrimaryProfiles([
      ...existing,
      { profileId: crypto.randomUUID(), label: '', nameFieldPath: '', emailFieldPath: '' },
    ]);
  };

  const updateProfile = (
    profileId: string,
    field: 'label' | 'nameFieldPath' | 'emailFieldPath',
    value: string,
  ) => {
    const existing = editedMappings?.contactsSchema?.primarySource?.senderProfiles ?? [];
    replacePrimaryProfiles(
      existing.map(p => (p.profileId === profileId ? { ...p, [field]: value } : p)),
    );
  };

  const removeProfile = (profileId: string) => {
    const existing = editedMappings?.contactsSchema?.primarySource?.senderProfiles ?? [];
    replacePrimaryProfiles(existing.filter(p => p.profileId !== profileId));
  };

  const source = activeMappings?.contactsSchema?.primarySource;
  const profiles = source?.senderProfiles ?? [];
  const schemaProperties = getSchemaProperties(schemas, source?.schemaId ?? '');

  return {
    isLoading,
    isEditing,
    isSaving: updateMutation.isPending,
    saveError,
    profiles,
    schemaProperties,
    handleEdit,
    handleCancel,
    handleSave,
    addProfile,
    updateProfile,
    removeProfile,
  };
}
